import ApiResponse from '@core/types/apiResponse';
import { ErrorResponse } from '@core/types/errorResponse';
import { normalizeAllForList } from '@core/utils/listQuery';
import type { ListService, ListServiceResult } from '@core/hooks/usePaginatedList';

export type ListValidationResult<P> = {
  isValid: boolean;
  processedParams: P;
  errors: string[];
};

/**
 * Build an error-shaped ApiResponse. Centralizes the per-service
 * `handle*Error` helpers that were duplicated across the list services.
 */
export function makeServiceErrorResponse<T = any>(error: unknown, title: string): ApiResponse<T> {
  const detail = error instanceof Error ? error.message : 'An unexpected error occurred';
  const errorResponse: ErrorResponse = { statusCode: 500, title, detail };

  return {
    data: null,
    error: errorResponse,
    status: 500,
    isSuccess: () => false,
    isError: () => true,
    getData: () => null,
    getError: () => errorResponse,
  } as ApiResponse<T>;
}

export interface CreateListServiceConfig<
  T,
  P extends { all?: boolean; page?: number; perPage?: number },
  M,
  R extends ListServiceResult<T, M>
> {
  /** Raw transport call returning the concrete `{ data, meta }` list envelope. */
  fetchPage: (params: P) => Promise<ApiResponse<R>>;
  /** Entity-specific query-param validation. */
  validateParams: (params: P) => ListValidationResult<P>;
  /** Optional per-row display mapper (adds computed display fields). */
  mapRow?: (item: T) => T;
  /** Filter keys driving `normalizeAllForList` (drops `all` when a filter is active) and the page-reset default. */
  filterKeys: (keyof P & string)[];
  /** Title used in fallback error responses, e.g. "Church Service Error". */
  errorTitle: string;
}

export interface BuiltListService<T, P, M, R extends ListServiceResult<T, M>>
  extends ListService<T, P, M> {
  /** Back-compat alias of `fetch` so existing `fetch*Async` exports keep their name and concrete response type. */
  fetchAsync: (params?: P) => Promise<ApiResponse<R>>;
  /** Back-compat alias of `process`. */
  processForDisplay: (items: T[]) => T[];
}

/**
 * Assemble a {@link ListService} (consumed by `usePaginatedList`) from an
 * entity's raw transport call, validation, and row mapper. Owns the shared
 * fetch wrapper (normalize → default pagination → try/catch error response)
 * and row processing that were previously copy-pasted per entity.
 */
export function createListService<
  T,
  P extends { all?: boolean; page?: number; perPage?: number },
  M,
  R extends ListServiceResult<T, M> = ListServiceResult<T, M>
>(config: CreateListServiceConfig<T, P, M, R>): BuiltListService<T, P, M, R> {
  const { fetchPage, validateParams, mapRow, filterKeys, errorTitle } = config;

  const fetchAsync = async (
    params: P = {} as P
  ): Promise<ApiResponse<R>> => {
    try {
      const normalized = normalizeAllForList(
        params as Record<string, any>,
        filterKeys as string[]
      ) as P;

      const filterActive = filterKeys.some((key) => {
        const value = normalized[key];
        return value !== undefined && value !== null && value !== '';
      });

      const processedParams: P = normalized.all
        ? ({ all: true, ...normalized } as P)
        : ({
            perPage: 10,
            ...normalized,
            page: filterActive ? 1 : ((normalized.page as number | undefined) ?? 1),
          } as P);

      return await fetchPage(processedParams);
    } catch (error) {
      return makeServiceErrorResponse<R>(error, errorTitle);
    }
  };

  const processForDisplay = (items: T[]): T[] => (mapRow ? items.map(mapRow) : items);

  return {
    validate: validateParams,
    fetch: fetchAsync,
    process: processForDisplay,
    fetchAsync,
    processForDisplay,
  };
}
